import React, { useState, useEffect } from "react";
import {
  Container, Typography, Button, Box, useMediaQuery, Grid, Chip, IconButton, Tooltip, Avatar
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CampaignIcon from '@mui/icons-material/Campaign';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AddIcon from '@mui/icons-material/Add';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import AnnouncementForm from './AnnouncementForm';
import { createAnnouncement, getAnnouncements, updateAnnouncement, deleteAnnouncement } from '../../api/announcementAPI';
import useSnackbar from '../../hooks/useSnackbar';
import moment from 'moment';

const CARD_GRADIENTS = [
  'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
  'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  'linear-gradient(135deg, #a18cd1 0%, #fbc2eb 100%)',
];

const Announcements = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { user } = useSelector((state) => state.tokens);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const [announcements, setAnnouncements] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ title: '', message: '', assignedUsers: [] });

  const loggedInUserIsAdminOrHR = user?.permission === "Admin" || user?.permission === "Admin-a" || user?.permission === "HR Management" || user?.permission === "HR";

  const fetchAnnouncements = () => {
    getAnnouncements()
      .then((res) => setAnnouncements(res.data.data))
      .catch((err) => console.error("Failed to fetch announcements:", err));
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  const handleAddClick = () => { setEditId(null); setFormData({ title: '', message: '', assignedUsers: [] }); setDialogOpen(true); };

  const handleEditClick = (ann) => {
    setEditId(ann._id);
    setFormData({ title: ann.title, message: ann.message, assignedUsers: ann.assignedUsers ? ann.assignedUsers.map(u => u._id) : [] });
    setDialogOpen(true);
  };

  const handleDeleteClick = (id) => {
    if (window.confirm("Are you sure you want to delete this announcement?")) {
      deleteAnnouncement(id)
        .then(() => { showSnackbar("Announcement deleted successfully", "success"); fetchAnnouncements(); })
        .catch(() => showSnackbar("Failed to delete announcement", "error"));
    }
  };

  const handleDialogClose = () => setDialogOpen(false);
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleUsersChange = (event) => {
    const { target: { value } } = event;
    setFormData({ ...formData, assignedUsers: typeof value === 'string' ? value.split(',') : value });
  };

  const handleAddSubmit = (e) => {
    e.preventDefault();
    if (!formData.title || !formData.message) { showSnackbar("Title and Message are required", "warning"); return; }
    const apiCall = editId ? updateAnnouncement(editId, formData) : createAnnouncement(formData);
    apiCall
      .then(() => { showSnackbar(editId ? "Updated successfully!" : "Created successfully!", "success"); setDialogOpen(false); fetchAnnouncements(); })
      .catch((err) => showSnackbar(err.response?.data?.message || "Operation failed", "error"));
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa', pb: 6 }}>
      {/* Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1a237e 0%, #4a148c 50%, #1565c0 100%)',
          pt: { xs: 4, md: 5 },
          pb: { xs: 6, md: 8 },
          px: { xs: 2, md: 4 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            right: '-10%',
            width: '400px',
            height: '400px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: '-30%',
            left: '-5%',
            width: '300px',
            height: '300px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.03)',
          }
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" justifyContent="space-between" alignItems="center" flexDirection={isMobile ? 'column' : 'row'} gap={3}>
            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <CampaignIcon sx={{ color: 'rgba(255,255,255,0.8)', fontSize: 28 }} />
                <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', letterSpacing: 3, fontSize: '0.75rem' }}>
                  COMPANY UPDATES
                </Typography>
              </Box>
              <Typography variant={isMobile ? "h4" : "h3"} fontWeight="900" sx={{ color: '#fff', lineHeight: 1.2 }}>
                Announcements
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 1 }}>
                {announcements.length} announcement{announcements.length !== 1 ? 's' : ''} available
              </Typography>
            </Box>
            {loggedInUserIsAdminOrHR && (
              <Button
                variant="contained"
                onClick={handleAddClick}
                startIcon={<AddIcon />}
                sx={{
                  width: isMobile ? '100%' : 'auto',
                  bgcolor: 'rgba(255,255,255,0.15)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(255,255,255,0.3)',
                  color: '#fff',
                  borderRadius: '50px',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  textTransform: 'none',
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.25)',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
                  }
                }}
              >
                Create Announcement
              </Button>
            )}
          </Box>
        </Container>
      </Box>

      {/* Cards Grid */}
      <Container maxWidth="lg" sx={{ mt: -4, position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {announcements.length > 0 ? (
            announcements.map((ann, idx) => {
              const gradient = CARD_GRADIENTS[idx % CARD_GRADIENTS.length];
              const initial = ann.createdBy?.fullName?.charAt(0).toUpperCase() || 'U';
              return (
                <Grid item xs={12} md={6} key={ann._id}>
                  <Box
                    sx={{
                      borderRadius: 4,
                      overflow: 'hidden',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
                      bgcolor: '#fff',
                      transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        transform: 'translateY(-6px)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                      }
                    }}
                  >
                    {/* Top Gradient Banner */}
                    <Box
                      sx={{
                        background: gradient,
                        p: 3,
                        position: 'relative',
                        minHeight: 110,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                      }}
                    >
                      {/* Top row: date + actions */}
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                        <Chip
                          label={moment(ann.createdAt).format("MMM DD, YYYY")}
                          size="small"
                          sx={{
                            bgcolor: 'rgba(255,255,255,0.22)',
                            color: '#fff',
                            fontWeight: 700,
                            backdropFilter: 'blur(4px)',
                            border: '1px solid rgba(255,255,255,0.3)',
                            fontSize: '0.7rem'
                          }}
                        />
                        {loggedInUserIsAdminOrHR && (
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEditClick(ann)}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.18)',
                                  color: '#fff',
                                  backdropFilter: 'blur(4px)',
                                  '&:hover': { bgcolor: 'rgba(255,255,255,0.35)' },
                                  width: 30, height: 30
                                }}
                              >
                                <EditIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteClick(ann._id)}
                                sx={{
                                  bgcolor: 'rgba(255,255,255,0.18)',
                                  color: '#fff',
                                  backdropFilter: 'blur(4px)',
                                  '&:hover': { bgcolor: 'rgba(220,53,69,0.6)' },
                                  width: 30, height: 30
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: 15 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        )}
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        fontWeight="800"
                        sx={{ color: '#fff', mt: 1.5, textShadow: '0 2px 8px rgba(0,0,0,0.2)', lineHeight: 1.3 }}
                      >
                        {ann.title}
                      </Typography>
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ p: 3 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#546e7a',
                          lineHeight: 1.75,
                          whiteSpace: 'pre-line',
                          mb: 2.5,
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          fontSize: '0.92rem'
                        }}
                      >
                        {ann.message}
                      </Typography>

                      {/* Footer */}
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          pt: 2,
                          borderTop: '1px solid rgba(0,0,0,0.06)'
                        }}
                      >
                        {/* Author */}
                        <Box display="flex" alignItems="center" gap={1.2}>
                          <Avatar
                            sx={{
                              width: 34,
                              height: 34,
                              background: gradient,
                              fontSize: '0.85rem',
                              fontWeight: 800,
                              boxShadow: '0 3px 8px rgba(0,0,0,0.15)'
                            }}
                          >
                            {initial}
                          </Avatar>
                          <Box>
                            <Typography variant="caption" sx={{ color: '#90a4ae', display: 'block', lineHeight: 1, mb: 0.3 }}>
                              Posted by
                            </Typography>
                            <Typography variant="subtitle2" fontWeight="700" sx={{ color: '#37474f', lineHeight: 1 }}>
                              {ann.createdBy?.fullName || 'Unknown'}
                            </Typography>
                          </Box>
                        </Box>

                        {/* Assigned users + time */}
                        <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.5}>
                          <Typography variant="caption" sx={{ color: '#90a4ae', fontSize: '0.68rem' }}>
                            {moment(ann.createdAt).format("hh:mm A")}
                          </Typography>
                          {loggedInUserIsAdminOrHR && (
                            <Chip
                              icon={<PeopleAltIcon sx={{ fontSize: '13px !important' }} />}
                              label={`${ann.assignedUsers?.length || 0} users`}
                              size="small"
                              sx={{
                                height: 22,
                                fontSize: '0.68rem',
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, rgba(103,126,234,0.12) 0%, rgba(118,75,162,0.12) 100%)',
                                color: '#5c35a5',
                                border: '1px solid rgba(118,75,162,0.2)',
                                '& .MuiChip-icon': { color: '#5c35a5' }
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              );
            })
          ) : (
            <Grid item xs={12}>
              <Box
                textAlign="center"
                py={10}
                sx={{
                  background: '#fff',
                  borderRadius: 4,
                  boxShadow: '0 4px 24px rgba(0,0,0,0.06)'
                }}
              >
                <CampaignIcon sx={{ fontSize: 64, color: '#dee2e6', mb: 2 }} />
                <Typography variant="h6" color="#adb5bd" fontWeight="600">
                  No announcements yet
                </Typography>
                <Typography variant="body2" color="#ced4da" mt={0.5}>
                  {loggedInUserIsAdminOrHR ? "Click 'Create Announcement' to get started." : "You have no announcements assigned to you."}
                </Typography>
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>

      <AnnouncementForm
        open={dialogOpen}
        handleClose={handleDialogClose}
        handleAddSubmit={handleAddSubmit}
        formData={formData}
        handleChange={handleChange}
        handleUsersChange={handleUsersChange}
        isEdit={!!editId}
      />

      <SnackbarComponent />
    </Box>
  );
};

export default Announcements;
